import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { ExternalLink, Loader2, Pencil } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/formatters";
import { isPhoneNumber, phoneForCopy } from "@/lib/phone";
import { CopyButton } from "@/components/shared/CopyButton";
import type { DetailField } from "@/api/types";

/** Save payload type — what the parent (DynamicDetailFields)
 *  forwards to its CompanyUpdate PATCH. */
export type FieldValue = string | number | boolean | null;

interface FieldRendererProps {
  field: DetailField;
  value: unknown;
  /** Save handler — receives the new typed value. Wired to PATCH
   *  Company[key] or Company.metadata[metadata_key] by the parent. */
  onSave?: (next: FieldValue) => Promise<void>;
  readOnly?: boolean;
  /** Suppress the built-in uppercase label. Lets callers drop the
   *  renderer into their own row layout (e.g. CompanyDetail's
   *  FieldRow grid) without ending up with two labels stacked. */
  hideLabel?: boolean;
}

/**
 * Render one detail field — label + value, with click-to-edit when
 * the field is editable. Type-discriminates on `field.type` so each
 * shape (text / number / select / boolean / currency / date / link /
 * textarea) gets the right editor and read-only formatter.
 *
 * Booleans render as an always-on Switch (no display ↔ edit
 * toggle); selects, dates and links open their bespoke editor on
 * click and commit immediately on change. Text-shape variants
 * (text / number / currency / textarea) follow a click-to-edit
 * pattern with blur / Enter to save and Escape to cancel.
 */
export function FieldRenderer({
  field,
  value,
  onSave,
  readOnly: readOnlyProp,
  hideLabel,
}: FieldRendererProps) {
  const readOnly = readOnlyProp || field.editable === false || !onSave;
  const type = (field.type ?? "text").toLowerCase();

  // Booleans are special: the Switch IS both the display and the
  // editor, so we skip the click-to-edit dance entirely.
  const inner =
    type === "boolean" || type === "bool" || type === "toggle" ? (
      <BooleanField value={value} readOnly={readOnly} onSave={onSave} />
    ) : (
      <ClickToEditField
        field={field}
        type={type}
        value={value}
        readOnly={readOnly}
        onSave={onSave}
      />
    );

  if (hideLabel) return inner;
  return <Row label={field.label}>{inner}</Row>;
}

function Row({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="mb-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </div>
      {children}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Click-to-edit wrapper                                                  */
/* --------------------------------------------------------------------- */

function ClickToEditField({
  field,
  type,
  value,
  readOnly,
  onSave,
}: {
  field: DetailField;
  type: string;
  value: unknown;
  readOnly: boolean;
  onSave?: (next: FieldValue) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (next: FieldValue) => {
    if (!onSave) {
      setEditing(false);
      return;
    }
    // No-op when nothing changed — close without a network round trip.
    const current = normalizeForCompare(value);
    if (next === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (err) {
      setError(extractMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (editing && !readOnly) {
    return (
      <div className="flex items-center gap-2">
        <FieldEditor
          field={field}
          type={type}
          value={value}
          saving={saving}
          onSave={handleSave}
          onCancel={() => {
            setEditing(false);
            setError(null);
          }}
        />
        {saving && (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" />
        )}
        {error && (
          <span className="text-xs text-rose-600" title={error}>
            !
          </span>
        )}
      </div>
    );
  }

  return (
    <FieldDisplay
      type={type}
      value={value}
      editable={!readOnly}
      onEdit={() => setEditing(true)}
      copyLabel={copyLabelFor(field, type)}
    />
  );
}

/** Phone numbers and emails get a copy button. The company's own number
 *  is configured as text, so its key counts too. */
function copyLabelFor(field: DetailField, type: string): string | null {
  if (type === "phone" || field.key === "main_phone") return "phone number";
  if (type === "email") return "email";
  return null;
}

/* --------------------------------------------------------------------- */
/* Read-mode display                                                      */
/* --------------------------------------------------------------------- */

function FieldDisplay({
  type,
  value,
  editable,
  onEdit,
  copyLabel,
}: {
  type: string;
  value: unknown;
  editable: boolean;
  onEdit: () => void;
  copyLabel: string | null;
}) {
  // A number or an email keeps its copy button out of the click-to-edit
  // button: the value and the pencil edit, the copy icon copies.
  if (
    copyLabel &&
    value != null &&
    String(value).trim() !== "" &&
    (copyLabel !== "phone number" || isPhoneNumber(String(value)))
  ) {
    const text = String(value);
    return (
      <div
        className={cn(
          "group flex h-8 min-w-0 items-center gap-1 rounded-md px-2",
          editable && "hover:bg-slate-50",
        )}
      >
        <button
          type="button"
          disabled={!editable}
          onClick={onEdit}
          className={cn("min-w-0 truncate text-left", !editable && "cursor-default")}
        >
          {renderDisplayValue(type, value)}
        </button>
        <CopyButton
          value={copyLabel === "phone number" ? phoneForCopy(text) : text.trim()}
          label={copyLabel}
        />
        {editable && (
          <button
            type="button"
            onClick={onEdit}
            aria-label="Edit"
            className="ml-auto shrink-0 rounded p-0.5 text-slate-300 group-hover:text-slate-500"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  // Links bypass the click-to-edit pattern entirely — the anchor
  // owns the click. The pencil sits to the right for editors.
  if (type === "url" || type === "link") {
    return (
      <div className="flex h-8 items-center gap-2 px-2">
        <UrlValue value={value} />
        {editable && (
          <button
            type="button"
            onClick={onEdit}
            className="rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-500"
            aria-label="Edit"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      disabled={!editable}
      onClick={onEdit}
      className={cn(
        "group flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-2 text-left",
        editable && "hover:bg-slate-50",
        !editable && "cursor-default",
      )}
    >
      <span className="min-w-0 flex-1 truncate">
        {renderDisplayValue(type, value)}
      </span>
      {editable && (
        <Pencil className="h-3 w-3 shrink-0 text-slate-300 group-hover:text-slate-500" />
      )}
    </button>
  );
}

function renderDisplayValue(type: string, value: unknown): ReactNode {
  if (value == null || value === "") {
    return <span className="text-sm italic text-slate-400">—</span>;
  }

  switch (type) {
    case "select":
    case "enum":
      return (
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
          {labelForOption(String(value))}
        </span>
      );

    case "currency":
      return (
        <span className="font-mono text-sm text-slate-700">
          {formatCurrency(toNumber(value))}
        </span>
      );

    case "number":
    case "integer":
    case "headcount": {
      const n = toNumber(value);
      return (
        <span className="text-sm text-slate-700">
          {Number.isFinite(n) ? formatNumber(n) : String(value)}
        </span>
      );
    }

    case "date":
    case "datetime":
      return (
        <span className="text-sm text-slate-700">
          {formatDate(value)}
        </span>
      );

    case "textarea":
      return (
        <span className="block whitespace-pre-wrap text-sm text-slate-700">
          {String(value)}
        </span>
      );

    case "text":
    case "string":
    case "email":
    case "phone":
    default:
      return <span className="text-sm text-slate-700">{String(value)}</span>;
  }
}

/* --------------------------------------------------------------------- */
/* Edit-mode editor                                                       */
/* --------------------------------------------------------------------- */

function FieldEditor({
  field,
  type,
  value,
  saving,
  onSave,
  onCancel,
}: {
  field: DetailField;
  type: string;
  value: unknown;
  saving: boolean;
  onSave: (next: FieldValue) => void;
  onCancel: () => void;
}) {
  // Selects need a live draft so the trigger label updates the
  // moment the user picks an option, even though we save right away.
  const [draft, setDraft] = useState<FieldValue>(() =>
    coerceForType(value, type),
  );

  // Re-seed the draft if the row remounts with a new value.
  useEffect(() => {
    setDraft(coerceForType(value, type));
  }, [value, type]);

  switch (type) {
    case "select":
    case "enum":
      return (
        <Select
          defaultOpen
          value={(draft as string | null) ?? ""}
          onValueChange={(v) => {
            setDraft(v);
            onSave(v);
          }}
          onOpenChange={(open) => {
            // Close without selecting → cancel back to display.
            if (!open && !saving) {
              setTimeout(onCancel, 0);
            }
          }}
        >
          <SelectTrigger autoFocus className="h-8 w-full text-sm">
            <SelectValue
              placeholder={`Select ${field.label.toLowerCase()}…`}
            />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((option) => (
              <SelectItem key={option} value={option}>
                {labelForOption(option)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "currency":
      return (
        <div className="flex items-center gap-1">
          <span className="text-sm text-slate-400">$</span>
          <Input
            autoFocus
            type="number"
            inputMode="decimal"
            value={draft === null || draft === undefined ? "" : String(draft)}
            onChange={(e) => setDraft(parseNumberInput(e.target.value))}
            onBlur={() => onSave(draft)}
            onKeyDown={(e) => handleKey(e, () => onSave(draft), onCancel)}
            disabled={saving}
            className="h-8 font-mono text-sm"
          />
        </div>
      );

    case "number":
    case "integer":
    case "headcount":
      return (
        <Input
          autoFocus
          type="number"
          inputMode="numeric"
          value={draft === null || draft === undefined ? "" : String(draft)}
          onChange={(e) => setDraft(parseNumberInput(e.target.value))}
          onBlur={() => onSave(draft)}
          onKeyDown={(e) => handleKey(e, () => onSave(draft), onCancel)}
          disabled={saving}
          className="h-8 text-sm"
        />
      );

    case "date":
    case "datetime":
      return (
        <Input
          autoFocus
          type="date"
          value={toDateInputValue(draft)}
          onChange={(e) => {
            const next = e.target.value || null;
            setDraft(next);
            onSave(next);
          }}
          onKeyDown={(e) => handleKey(e, () => onSave(draft), onCancel)}
          disabled={saving}
          className="h-8 text-sm"
        />
      );

    case "textarea":
      return (
        <TextareaEditor
          value={(draft as string | null) ?? ""}
          saving={saving}
          onSave={(v) => onSave(v)}
          onCancel={onCancel}
        />
      );

    case "url":
    case "link":
    case "email":
    case "phone":
    case "text":
    case "string":
    default:
      return (
        <Input
          autoFocus
          type="text"
          value={(draft as string | null) ?? ""}
          onChange={(e) =>
            setDraft(e.target.value === "" ? null : e.target.value)
          }
          onBlur={() => onSave(draft)}
          onKeyDown={(e) => handleKey(e, () => onSave(draft), onCancel)}
          disabled={saving}
          className="h-8 text-sm"
        />
      );
  }
}

/* --------------------------------------------------------------------- */
/* Boolean field — always-on Switch                                       */
/* --------------------------------------------------------------------- */

function BooleanField({
  value,
  readOnly,
  onSave,
}: {
  value: unknown;
  readOnly: boolean;
  onSave?: (next: FieldValue) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const checked = !!value;

  const handleToggle = async (next: boolean) => {
    if (!onSave) return;
    setSaving(true);
    try {
      await onSave(next);
    } catch {
      // Errors are toasted upstream by the parent saveField wrapper.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-8 items-center gap-2 px-2">
      <Switch
        checked={checked}
        disabled={readOnly || saving}
        onCheckedChange={handleToggle}
      />
      <span
        className={cn(
          "text-xs font-medium",
          checked ? "text-emerald-600" : "text-slate-400",
        )}
      >
        {checked ? "Yes" : "No"}
      </span>
      {saving && (
        <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
      )}
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* Textarea editor                                                        */
/* --------------------------------------------------------------------- */

function TextareaEditor({
  value,
  saving,
  onSave,
  onCancel,
}: {
  value: string;
  saving: boolean;
  onSave: (next: FieldValue) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  return (
    <textarea
      ref={ref}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onSave(draft === "" ? null : draft)}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
      disabled={saving}
      className="min-h-[80px] w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
    />
  );
}

/* --------------------------------------------------------------------- */
/* Link display                                                           */
/* --------------------------------------------------------------------- */

function UrlValue({ value }: { value: unknown }) {
  if (value == null || value === "") {
    return <span className="text-sm italic text-slate-400">—</span>;
  }
  const raw = String(value);
  const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-w-0 items-center gap-1 truncate text-sm text-indigo-600 hover:underline"
    >
      <span className="truncate">{raw}</span>
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  );
}

/* --------------------------------------------------------------------- */
/* Helpers                                                                */
/* --------------------------------------------------------------------- */

function handleKey(
  e: KeyboardEvent<HTMLInputElement>,
  onEnter: () => void,
  onEscape: () => void,
) {
  if (e.key === "Enter") {
    e.preventDefault();
    onEnter();
  } else if (e.key === "Escape") {
    e.preventDefault();
    onEscape();
  }
}

function coerceForType(value: unknown, type: string): FieldValue {
  if (value == null) return null;
  if (type === "number" || type === "integer" || type === "headcount" || type === "currency") {
    if (typeof value === "number") return value;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  if (type === "boolean" || type === "bool" || type === "toggle") {
    return !!value;
  }
  return typeof value === "string" ? value : String(value);
}

function normalizeForCompare(value: unknown): FieldValue {
  if (value == null || value === "") return null;
  if (typeof value === "number" || typeof value === "boolean") return value;
  return typeof value === "string" ? value : String(value);
}

function parseNumberInput(raw: string): FieldValue {
  if (raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function toNumber(value: unknown): number {
  if (typeof value === "number") return value;
  return Number(value);
}

function toDateInputValue(value: FieldValue): string {
  if (typeof value !== "string" || value === "") return "";
  // Backend may send a full ISO datetime — strip to YYYY-MM-DD for
  // the native date input.
  return value.length >= 10 ? value.substring(0, 10) : value;
}

/** "Apr 15, 2026" — graceful "—" on unparseable input. */
function formatDate(value: unknown): string {
  if (value == null || value === "") return "—";
  const raw = typeof value === "string" ? value : String(value);
  const parsed = raw.length === 10 ? parseISO(raw) : new Date(raw);
  return isValid(parsed) ? format(parsed, "MMM d, yyyy") : raw;
}

/** $42M / $250K / $0 — UI-friendly currency display. */
function formatCurrency(n: number): string {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1_000_000)
    return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${formatNumber(n)}`;
}

function humanize(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Curated labels for well-known select option VALUES (CFO v2 team /
 *  hiring / funding levels). These are scoring-specific enum values
 *  that humanize() would render awkwardly ("cfo" → "Cfo",
 *  "senior_cfo" → "Senior Cfo"). Anything not listed falls back to
 *  humanize, so other selects (tax_id_type, …) are unaffected. */
const OPTION_LABELS: Record<string, string> = {
  none: "None",
  junior: "Junior",
  senior: "Senior",
  cfo: "CFO in place",
  senior_cfo: "CFO (recruiting)",
  love_money: "Love Money",
  pre_seed: "Pre-seed",
  seed: "Seed",
  series_a: "Series A",
  series_b: "Series B",
  series_c: "Series C",
  series_d: "Series D",
  growth: "Growth",
  ipo: "IPO",
};

function labelForOption(value: string): string {
  return OPTION_LABELS[value] ?? humanize(value);
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return "Save failed";
}
